import { StatusDot } from "@/components/ui/status-dot";
import { cn } from "@/lib/utils";
import type { McpSessionRecord } from "@/stores/mcpSessionStore";
import { Plug } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type SessionStatus = McpSessionRecord["status"] | "idle";

const DEFAULT_HIDDEN_STATUSES: SessionStatus[] = ["connected"];
const DEFAULT_SESSION_COLOR_MAP: Record<SessionStatus, string> = {
  connected: "bg-success",
  connecting: "bg-info",
  error: "bg-destructive",
  idle: "bg-muted",
};

interface UseStatusVisibilityOptions {
  delayStatuses?: SessionStatus[];
  hiddenStatuses?: SessionStatus[];
  showDelayMs?: number;
  lingerDurationMs?: number;
}

interface McpConnectionStatusProps extends UseStatusVisibilityOptions {
  status: SessionStatus;
  description?: ReactNode;
  className?: string;
  colorMap?: Record<SessionStatus, string>;
}

export function McpConnectionStatus({
  status,
  description,
  className,
  colorMap = DEFAULT_SESSION_COLOR_MAP,
  hiddenStatuses = DEFAULT_HIDDEN_STATUSES,
  showDelayMs = 300,
  lingerDurationMs = 1000, // Changed from 3000ms to 1000ms as requested
}: Omit<McpConnectionStatusProps, "delayStatuses">) {
  const { isVisible, isFading } = useStatusVisibilityWithAnimation(status, {
    hiddenStatuses,
    showDelayMs,
    lingerDurationMs,
  });

  const label = useMemo(() => sessionStatusLabel(status), [status]);

  if (!isVisible && !isFading) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col items-end gap-1 text-right",
        isVisible
          ? isFading
            ? "animate-fadeIn"
            : "opacity-100"
          : isFading
            ? "opacity-0 transition-opacity duration-300 ease-in-out"
            : "opacity-0",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Plug className="h-4 w-4" />
          <span>MCP connection:</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
          <StatusDot
            status={status}
            colorMap={colorMap}
            pulse={status === "connecting"}
            ariaLabel={`MCP session status: ${label}`}
          />
          <span>{label}</span>
        </span>
      </div>
      {description ? (
        <div className="text-xs text-muted-foreground">{description}</div>
      ) : null}
    </div>
  );
}

type TimeoutHandle = ReturnType<typeof setTimeout>;

function useStatusVisibilityWithAnimation(
  status: SessionStatus,
  {
    hiddenStatuses = DEFAULT_HIDDEN_STATUSES,
    showDelayMs = 300,
    lingerDurationMs = 1000,
  }: Omit<UseStatusVisibilityOptions, "delayStatuses">,
) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const timersRef = useRef<{
    show?: TimeoutHandle;
    hide?: TimeoutHandle;
    fadeOut?: TimeoutHandle;
  }>({});

  const clearTimers = useCallback(() => {
    if (timersRef.current.show) {
      clearTimeout(timersRef.current.show);
      timersRef.current.show = undefined;
    }

    if (timersRef.current.hide) {
      clearTimeout(timersRef.current.hide);
      timersRef.current.hide = undefined;
    }

    if (timersRef.current.fadeOut) {
      clearTimeout(timersRef.current.fadeOut);
      timersRef.current.fadeOut = undefined;
    }
  }, []);

  useEffect(() => {
    const hiddenSet = new Set(hiddenStatuses);
    const shouldBeVisible = !hiddenSet.has(status);

    clearTimers();

    if (shouldBeVisible) {
      // Status should be visible
      if (visible) {
        // Already visible, nothing to change
        return () => clearTimers();
      }

      // Start fade-in process after delay
      timersRef.current.show = setTimeout(() => {
        setAnimating(true);
        setVisible(true);

        // Reset animating state after fade-in animation completes
        timersRef.current.fadeOut = setTimeout(() => {
          setAnimating(false);
          timersRef.current.fadeOut = undefined;
        }, 300);

        timersRef.current.show = undefined;
      }, showDelayMs);

      return () => clearTimers();
    }

    // Status should be hidden
    if (!visible) {
      // Already hidden, nothing to do
      return () => clearTimers();
    }

    // Start hide process after linger duration
    timersRef.current.hide = setTimeout(() => {
      setAnimating(true);
      setVisible(false);

      // Stop animating after fade-out animation duration (300ms)
      timersRef.current.fadeOut = setTimeout(() => {
        setAnimating(false);
        timersRef.current.fadeOut = undefined;
      }, 300);

      timersRef.current.hide = undefined;
    }, lingerDurationMs);

    return () => clearTimers();
  }, [
    clearTimers,
    hiddenStatuses,
    lingerDurationMs,
    showDelayMs,
    status,
    visible,
  ]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    isVisible: visible,
    isFading: animating,
  };
}

function sessionStatusLabel(status: SessionStatus) {
  switch (status) {
    case "connected":
      return "Connected";
    case "connecting":
      return "Connecting";
    case "error":
      return "Error";
    default:
      return "Not Connected";
  }
}
